var _ = require('optimizely-client-fn');
var ApiEvents = require('core/api_events');
var createError = require('core/utils/custom_error').create;
var DocumentEnv = require('core/env/document');
var matches = require('core/utils/matches_selector');

var ClickDelegateError = exports.Error = createError('ClickDelegateError');

/**
 * @constructor
 * @param {Function} handler whenever a click event matches a registered UserEvent
 */
function ClickDelegateClass(handler:Function) {
  this.handler = handler;
  this.events = [];
  this.unlistenFn = null;

  /**
   * Click event handler to invoke whenever a click event matches any registered event
   * @param {Event} evt
   */
  this.clickHandler = _.bind(function(evt:MouseEvent) {
    _.forEach(this.events, _.bind(function(userEvent:EventSchema) {
      try {
        // build in forward compatibility for events using the `config` field
        var selector = (userEvent.config && userEvent.config.selector)
          ? userEvent.config.selector
          : userEvent.eventFilter.selector;

        if (matchesSelector(evt, selector, userEvent)) {
          this.handler(userEvent);
        }
      } catch (e) {
        ApiEvents.emitError(new ClickDelegateError('Unable to handle click for selector' + selector + ':' + e.message));
      }
    }, this));
  }, this);
}

/**
 * Attaches a click event listener to document
 */
ClickDelegateClass.prototype.listen = function() {
  this.unlistenFn = (<Document>DocumentEnv).addEventListener('click', this.clickHandler, true);
};

/**
 * Removes the click event listener from document if exists
 */
ClickDelegateClass.prototype.unlisten = function() {
  if (this.unlistenFn) {
    this.unlistenFn();
    this.unlistenFn = null;
  }
};

/**
 * Returns true if any events are registered
 * @return {boolean}
 */
ClickDelegateClass.prototype.hasEvents = function(): boolean {
  return this.events.length > 0;
};

/**
 * Registers a click Event with the delegate
 * @param {Event} event
 */
ClickDelegateClass.prototype.addEvent = function(event:EventSchema) {
  this.events.push(event);
};

/**
 * Unregisters a click Event with the delegate
 * @param {Event} event
 */
ClickDelegateClass.prototype.removeEvent = function(event:EventSchema) {
  this.events = _.filter(this.events, function(evt) {
    return evt.apiName !== event.apiName;
  });
};

/**
 * Helper function to check if an event.target matches selector
 * @param {Event} evt
 * @param {string} selector
 * @param {Event} userEvent
 * @return {boolean}
 */
function matchesSelector(evt:Event, selector:string, userEvent:EventSchema): boolean {
  var element = evt.target;
  var numParentsTraversed = 0;

  if (__EXPERIMENTAL_SHADOW_DOM__ && (element as Element).shadowRoot) {
    // Trying to get the first element from the composed Path
    // This will only work with open shadow roots https://developer.mozilla.org/en-US/docs/Web/API/ShadowRoot/mode#open
    element = evt.composedPath()[0];
  }

  while (element) {
    var elMatches;

    try {
      elMatches = matches(element, selector);
    } catch (e) {
      var errorMetadata = {
        typeofElementValue: typeof element,
        nodeName: _.result(element, ['nodeName'], null),
        nodeType: _.result(element, ['nodeType'], null),
        targetName: _.result(evt, ['target', 'nodeName'], null),
        targetType: _.result(evt, ['target', 'nodeType'], null),
        numParentsTraversed: numParentsTraversed,
        selector: selector,
        errorMessage: e.message,
        eventId: userEvent.id,
      };

      ApiEvents.emitError(new ClickDelegateError('Unable to evaluate match for element'), errorMetadata);
      return false;
    }

    if (elMatches) {
      return true;
    }
    element = (<HTMLElement>element).parentElement;
    numParentsTraversed++;
  }
  return false;
}

module.exports = ClickDelegateClass;
